import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminProposals } from '../lib/adminFixtures';
import { AdminProposalSummary } from '../lib/adminTypes';
import { ExportPacketModal } from '../components/ExportPacketModal';
import { Search, Download, Clock, AlertTriangle, CheckCircle2, FileBox, Sparkles, CheckSquare, Activity, X, CircleDashed, ChevronRight, ListFilter, Bell, Milestone, MoreHorizontal, CalendarDays, Upload, MessageSquare, Flag, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}
// Mock milestone data keyed by proposal ID
const milestoneData: Record<string, {
  label: string;
  dueDate: string;
  reminderDays?: number;
}> = {
  '1': {
    label: 'Research Strategy draft due',
    dueDate: '2026-02-25',
    reminderDays: 3
  },
  '3': {
    label: 'Budget justification review',
    dueDate: '2026-03-10'
  },
  '4': {
    label: 'Biosketch revisions due',
    dueDate: '2026-03-01',
    reminderDays: 5
  },
  '5': {
    label: 'Subcontract agreements due',
    dueDate: '2026-03-15'
  },
  '6': {
    label: 'Specific Aims finalization',
    dueDate: '2026-03-05',
    reminderDays: 7
  },
  '7': {
    label: 'IRB approval submission',
    dueDate: '2026-04-01'
  },
  '8': {
    label: 'HRPO approval upload',
    dueDate: '2026-02-20',
    reminderDays: 2
  },
  '9': {
    label: 'Mentor letter finalization',
    dueDate: '2026-03-20'
  },
  '11': {
    label: 'Collaborator letters due',
    dueDate: '2026-03-10',
    reminderDays: 4
  },
  '12': {
    label: 'Multi-site agreement sign-off',
    dueDate: '2026-04-01'
  },
  '13': {
    label: 'Specific Aims page review',
    dueDate: '2026-04-15',
    reminderDays: 10
  },
  '14': {
    label: 'Core B budget revision',
    dueDate: '2026-02-22',
    reminderDays: 2
  },
  '16': {
    label: 'Research Strategy revision',
    dueDate: '2026-03-25'
  },
  '17': {
    label: 'TechPort registration',
    dueDate: '2026-03-15',
    reminderDays: 5
  },
  '19': {
    label: 'IRB protocol submission',
    dueDate: '2026-03-20',
    reminderDays: 7
  },
  '20': {
    label: 'QAPP final review',
    dueDate: '2026-04-15'
  },
  '21': {
    label: 'Data management plan upload',
    dueDate: '2026-02-28',
    reminderDays: 3
  },
  '22': {
    label: 'Training plan finalization',
    dueDate: '2026-03-15'
  },
  '23': {
    label: 'LOI revision & submission',
    dueDate: '2026-04-01',
    reminderDays: 8
  },
  '24': {
    label: 'Collaboration plan with MIT',
    dueDate: '2026-04-20'
  }
};
type MetricFilterKey = 'active' | 'at-risk' | 'due-soon' | 'ready-review' | 'ai-issues' | null;
export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [riskFilter, setRiskFilter] = useState<string | null>(null);
  const [piFilter, setPiFilter] = useState<string | null>(null);
  const [funderFilter, setFunderFilter] = useState<string | null>(null);
  const [deptFilter, setDeptFilter] = useState<string | null>(null);
  const [hideSubmitted, setHideSubmitted] = useState(false);
  const [metricFilter, setMetricFilter] = useState<MetricFilterKey>(null);
  const [selectedProposal, setSelectedProposal] = useState<AdminProposalSummary | null>(null);
  const [isActivityFeedOpen, setIsActivityFeedOpen] = useState(false);
  const [activityPopoverProposalId, setActivityPopoverProposalId] = useState<string | null>(null);
  const [activityPopoverPos, setActivityPopoverPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const uniquePIs = useMemo(() => [...new Set(adminProposals.map(p => p.piName))].sort(), []);
  const uniqueFunders = useMemo(() => [...new Set(adminProposals.map(p => p.funder))].sort(), []);
  const uniqueDepts = useMemo(() => [...new Set(adminProposals.map(p => p.department))].sort(), []);
  const metrics = useMemo(() => {
    const active = adminProposals.filter(p => p.status === 'In Progress' || p.status === 'Draft').length;
    const atRisk = adminProposals.filter(p => p.riskStatus === 'at-risk').length;
    const dueSoon = adminProposals.filter(p => daysUntil(p.deadlineISO) <= 30 && p.status !== 'Submitted' && p.status !== 'Under Review' && p.status !== 'Needs Review').length;
    const readyForReview = adminProposals.filter(p => p.status === 'Needs Review').length;
    const aiIssues = adminProposals.filter(p => p.aiIssueCount > 0).length;
    return {
      active,
      atRisk,
      dueSoon,
      readyForReview,
      aiIssues
    };
  }, []);
  const handleMetricClick = (key: MetricFilterKey) => {
    if (metricFilter === key) {
      setMetricFilter(null);
      setStatusFilter(null);
      setRiskFilter(null);
      setHideSubmitted(false);
    } else {
      setMetricFilter(key);
      setStatusFilter(null);
      setRiskFilter(null);
      setHideSubmitted(false);
    }
  };
  const filteredProposals = useMemo(() => {
    return adminProposals.filter(p => {
      if (metricFilter === 'active') {
        if (p.status !== 'In Progress' && p.status !== 'Draft') return false;
      }
      if (metricFilter === 'at-risk') {
        if (p.riskStatus !== 'at-risk') return false;
      }
      if (metricFilter === 'due-soon') {
        if (daysUntil(p.deadlineISO) > 30 || p.status === 'Submitted' || p.status === 'Under Review') return false;
      }
      if (metricFilter === 'ready-review') {
        if (p.status !== 'Needs Review') return false;
      }
      if (metricFilter === 'ai-issues') {
        if (p.aiIssueCount <= 0) return false;
      }
      if (hideSubmitted && (p.status === 'Submitted' || p.status === 'Under Review' || p.status === 'Needs Review')) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (riskFilter && p.riskStatus !== riskFilter) return false;
      if (piFilter && p.piName !== piFilter) return false;
      if (funderFilter && p.funder !== funderFilter) return false;
      if (deptFilter && p.department !== deptFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return p.title.toLowerCase().includes(query) || p.piName.toLowerCase().includes(query) || p.funder.toLowerCase().includes(query) || p.department.toLowerCase().includes(query) || p.mechanism.toLowerCase().includes(query);
      }
      return true;
    }).sort((a, b) => {
      if (a.riskStatus === 'at-risk' && b.riskStatus !== 'at-risk') return -1;
      if (a.riskStatus !== 'at-risk' && b.riskStatus === 'at-risk') return 1;
      if (a.riskStatus === 'needs-attention' && b.riskStatus !== 'needs-attention') return -1;
      if (a.riskStatus !== 'needs-attention' && b.riskStatus === 'needs-attention') return 1;
      return new Date(a.deadlineISO).getTime() - new Date(b.deadlineISO).getTime();
    });
  }, [searchQuery, statusFilter, riskFilter, piFilter, funderFilter, deptFilter, hideSubmitted, metricFilter]);
  const activityFeed = useMemo(() => {
    return adminProposals.map(p => ({
      id: p.id,
      piName: p.piName,
      title: p.title,
      activity: p.lastActivity,
      time: p.lastActivityTime,
      riskStatus: p.riskStatus,
      timestamp: p.lastActivityTime.includes('m') ? Date.now() - parseInt(p.lastActivityTime) * 60000 : p.lastActivityTime.includes('h') ? Date.now() - parseInt(p.lastActivityTime) * 3600000 : p.lastActivityTime.includes('d') ? Date.now() - parseInt(p.lastActivityTime) * 86400000 : Date.now() - 604800000
    })).sort((a, b) => b.timestamp - a.timestamp).slice(0, 12);
  }, []);
  const hasActiveFilters = statusFilter || riskFilter || piFilter || funderFilter || deptFilter || hideSubmitted || searchQuery || metricFilter;
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter(null);
    setRiskFilter(null);
    setPiFilter(null);
    setFunderFilter(null);
    setDeptFilter(null);
    setHideSubmitted(false);
    setMetricFilter(null);
  };
  const activeFilterPills: {
    label: string;
    onRemove: () => void;
  }[] = [];
  if (metricFilter) {
    const metricLabels: Record<string, string> = {
      active: 'Active Proposals',
      'at-risk': 'At Risk',
      'due-soon': 'Due ≤ 30 Days',
      'ready-review': 'Ready for Routing',
      'ai-issues': 'With Issues'
    };
    activeFilterPills.push({
      label: metricLabels[metricFilter],
      onRemove: () => setMetricFilter(null)
    });
  }
  if (statusFilter) activeFilterPills.push({
    label: `Status: ${statusFilter}`,
    onRemove: () => setStatusFilter(null)
  });
  if (riskFilter) {
    const riskLabels: Record<string, string> = {
      'at-risk': 'At Risk',
      'needs-attention': 'Needs Attention',
      'on-track': 'On Track'
    };
    activeFilterPills.push({
      label: `Risk: ${riskLabels[riskFilter] || riskFilter}`,
      onRemove: () => setRiskFilter(null)
    });
  }
  if (piFilter) activeFilterPills.push({
    label: `PI: ${piFilter}`,
    onRemove: () => setPiFilter(null)
  });
  if (funderFilter) activeFilterPills.push({
    label: `Funder: ${funderFilter}`,
    onRemove: () => setFunderFilter(null)
  });
  if (deptFilter) activeFilterPills.push({
    label: `Dept: ${deptFilter}`,
    onRemove: () => setDeptFilter(null)
  });
  if (hideSubmitted) activeFilterPills.push({
    label: 'Hiding Submitted',
    onRemove: () => setHideSubmitted(false)
  });
  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Under Review':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Needs Review':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Draft':
        return 'bg-slate-50 text-slate-600 border-slate-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };
  const getRiskBadgeStyle = (risk: string) => {
    switch (risk) {
      case 'at-risk':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'needs-attention':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };
  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'at-risk':
        return 'At Risk';
      case 'needs-attention':
        return 'Needs Attn';
      default:
        return 'On Track';
    }
  };
  const getRiskSparkleColor = (risk: string) => {
    switch (risk) {
      case 'at-risk':
        return 'text-red-500';
      case 'needs-attention':
        return 'text-amber-500';
      default:
        return 'text-emerald-500';
    }
  };
  const metricCards: {
    key: MetricFilterKey;
    label: string;
    value: number;
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
    valueColor: string;
    activeBorder: string;
    activeRing: string;
    showAiBadge?: boolean;
  }[] = [{
    key: 'active',
    label: 'Active Proposals',
    value: metrics.active,
    icon: FileBox,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    valueColor: 'text-foreground',
    activeBorder: 'border-indigo-400',
    activeRing: 'ring-indigo-100'
  }, {
    key: 'at-risk',
    label: 'At Risk',
    value: metrics.atRisk,
    icon: AlertTriangle,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
    valueColor: 'text-red-600',
    activeBorder: 'border-red-400',
    activeRing: 'ring-red-100',
    showAiBadge: true
  }, {
    key: 'due-soon',
    label: 'Due ≤ 30 Days',
    value: metrics.dueSoon,
    icon: Clock,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    valueColor: 'text-amber-600',
    activeBorder: 'border-amber-400',
    activeRing: 'ring-amber-100'
  }, {
    key: 'ready-review',
    label: 'Ready for Routing',
    value: metrics.readyForReview,
    icon: CheckCircle2,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    valueColor: 'text-emerald-600',
    activeBorder: 'border-emerald-400',
    activeRing: 'ring-emerald-100'
  }, {
    key: 'ai-issues',
    label: 'With Issues',
    value: metrics.aiIssues,
    icon: Sparkles,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-500',
    valueColor: 'text-violet-600',
    activeBorder: 'border-violet-400',
    activeRing: 'ring-violet-100',
    showAiBadge: true
  }];
  // Demo activity history for proposal '1'
  const demoActivityHistory = [{
    id: 'a1',
    date: 'Feb 19, 2026',
    time: '10:32 AM',
    actor: 'Origami AI',
    role: 'System',
    event: 'AI detected missing HRPO approval — flagged as at-risk',
    type: 'ai'
  }, {
    id: 'a2',
    date: 'Feb 19, 2026',
    time: '10:35 AM',
    actor: 'Maria Santos',
    role: 'Admin',
    event: 'Sent reminder to Dr. James Park: HRPO approval required before submission',
    type: 'flag'
  }, {
    id: 'a3',
    date: 'Feb 18, 2026',
    time: '3:15 PM',
    actor: 'Dr. James Park',
    role: 'PI',
    event: 'Uploaded Research Strategy draft (v1)',
    type: 'upload'
  }, {
    id: 'a4',
    date: 'Feb 18, 2026',
    time: '3:18 PM',
    actor: 'Origami AI',
    role: 'System',
    event: 'AI detected 3 compliance issues in Research Strategy: font size, line spacing, margin violation',
    type: 'ai'
  }, {
    id: 'a5',
    date: 'Feb 17, 2026',
    time: '11:00 AM',
    actor: 'Dr. Sarah Chen',
    role: 'Co-I',
    event: 'Uploaded Biosketch',
    type: 'upload'
  }, {
    id: 'a6',
    date: 'Feb 16, 2026',
    time: '2:45 PM',
    actor: 'Maria Santos',
    role: 'Admin',
    event: 'Marked Budget Justification as reviewed',
    type: 'action'
  }, {
    id: 'a7',
    date: 'Feb 14, 2026',
    time: '9:20 AM',
    actor: 'Dr. James Park',
    role: 'PI',
    event: 'Added comment: "Awaiting RT confirmation on subcontract scope"',
    type: 'comment'
  }, {
    id: 'a8',
    date: 'Feb 12, 2026',
    time: '4:00 PM',
    actor: 'Dr. Robert Torres',
    role: 'Co-I',
    event: 'Uploaded Specific Aims page',
    type: 'upload'
  }, {
    id: 'a9',
    date: 'Feb 10, 2026',
    time: '1:30 PM',
    actor: 'Maria Santos',
    role: 'Admin',
    event: 'Opened proposal record with SPO (intake complete)',
    type: 'action'
  }, {
    id: 'a10',
    date: 'Feb 8, 2026',
    time: '10:00 AM',
    actor: 'Dr. James Park',
    role: 'PI',
    event: 'Created proposal workspace in Origami',
    type: 'action'
  }];
  const activityTypeConfig: Record<string, {
    icon: React.ElementType;
    iconCls: string;
    dotCls: string;
  }> = {
    upload: {
      icon: Upload,
      iconCls: 'text-indigo-500',
      dotCls: 'bg-indigo-400'
    },
    ai: {
      icon: Bot,
      iconCls: 'text-violet-500',
      dotCls: 'bg-violet-400'
    },
    action: {
      icon: CheckCircle2,
      iconCls: 'text-emerald-500',
      dotCls: 'bg-emerald-400'
    },
    flag: {
      icon: Flag,
      iconCls: 'text-amber-500',
      dotCls: 'bg-amber-400'
    },
    comment: {
      icon: MessageSquare,
      iconCls: 'text-slate-500',
      dotCls: 'bg-slate-400'
    }
  };
  const handleActivityButtonClick = (e: React.MouseEvent, proposalId: string, isFirstRow: boolean) => {
    e.stopPropagation();
    if (!isFirstRow) return; // only demo for first visible row
    if (activityPopoverProposalId === proposalId) {
      setActivityPopoverProposalId(null);
      setActivityPopoverPos(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setActivityPopoverPos({
      top: rect.bottom + 8,
      left: Math.min(rect.left - 280, window.innerWidth - 360)
    });
    setActivityPopoverProposalId(proposalId);
  };
  return <div className="min-h-full bg-muted/40 flex flex-col h-full overflow-hidden" data-id="element-758">
      {/* Header + Metrics */}
      <div className="bg-white border-b border-border px-6 py-5 flex-shrink-0" data-id="element-759">
        <div className="max-w-[1600px] mx-auto w-full" data-id="element-760">
          <div className="flex items-start justify-between mb-5" data-id="element-761">
            <div data-id="element-762">
              <h1 className="text-xl font-bold text-foreground tracking-tight" data-id="element-763">
                Pre-Award Proposals in Flight
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5" data-id="element-764">
                UC San Francisco — Office of Sponsored Research
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-765">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5 text-xs h-8" onClick={() => setIsActivityFeedOpen(!isActivityFeedOpen)} data-id="element-766">
                <Activity className="h-3.5 w-3.5" data-id="element-767" />
                {isActivityFeedOpen ? 'Hide Feed' : 'Activity Feed'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3" data-id="element-768">
            {metricCards.map(metric => {
            const Icon = metric.icon;
            const isActive = metricFilter === metric.key;
            return <button key={metric.label} onClick={() => handleMetricClick(metric.key)} className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-white text-left transition-all duration-150 ${isActive ? `${metric.activeBorder} ring-2 ${metric.activeRing} shadow-sm` : 'border-border hover:border-muted-foreground/20 hover:shadow-sm'}`} data-id="element-769">
                  <div className={`p-2 rounded-lg ${metric.iconBg}`} data-id="element-770">
                    <Icon className={`h-4 w-4 ${metric.iconColor}`} data-id="element-771" />
                  </div>
                  <div className="min-w-0" data-id="element-772">
                    <div className="flex items-center gap-1 mb-1" data-id="element-773">
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider leading-none" data-id="element-774">
                        {metric.label}
                      </p>
                      {metric.showAiBadge && <span className="inline-flex items-center gap-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded px-1 py-0 text-[9px] font-medium leading-none h-3.5" data-id="element-775">
                          <Sparkles className="h-2 w-2" data-id="element-776" />
                          AI
                        </span>}
                    </div>
                    <p className={`text-xl font-bold leading-none ${metric.valueColor}`} data-id="element-777">
                      {metric.value}
                    </p>
                  </div>
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" data-id="element-778">
        <div className="flex-1 flex flex-col min-h-0" data-id="element-779">
          {/* Toolbar */}
          <div className="border-b border-border flex flex-col bg-white flex-shrink-0" data-id="element-780">
            <div className="max-w-[1600px] mx-auto w-full px-5 py-2.5" data-id="element-781">
              <div className="flex items-center justify-between gap-3" data-id="element-782">
                <div className="flex items-center gap-2 flex-1" data-id="element-783">
                  <div className="relative max-w-xs w-full" data-id="element-784">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" data-id="element-785" />
                    <input type="text" placeholder="Search proposals, PIs, funders..." className="w-full pl-8 pr-3 py-1.5 text-sm border border-input rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-786" />
                  </div>
                  <div className="h-5 w-px bg-border" data-id="element-787" />
                  <select className="text-xs border border-input rounded-md px-2.5 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={statusFilter || ''} onChange={e => setStatusFilter(e.target.value || null)} data-id="element-788">
                    <option value="" data-id="element-789">All Statuses</option>
                    <option value="In Progress" data-id="element-790">In Progress</option>
                    <option value="Draft" data-id="element-791">Draft</option>
                    <option value="Needs Review" data-id="element-792">Needs Review</option>
                    <option value="Submitted" data-id="element-793">Submitted</option>
                    <option value="Under Review" data-id="element-794">Under Review</option>
                  </select>
                  <select className="text-xs border border-input rounded-md px-2.5 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={riskFilter || ''} onChange={e => setRiskFilter(e.target.value || null)} data-id="element-795">
                    <option value="" data-id="element-796">All Risks</option>
                    <option value="at-risk" data-id="element-797">At Risk</option>
                    <option value="needs-attention" data-id="element-798">Needs Attention</option>
                    <option value="on-track" data-id="element-799">On Track</option>
                  </select>
                  <select className="text-xs border border-input rounded-md px-2.5 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={piFilter || ''} onChange={e => setPiFilter(e.target.value || null)} data-id="element-800">
                    <option value="" data-id="element-801">All PIs</option>
                    {uniquePIs.map(pi => <option key={pi} value={pi} data-id="element-802">
                        {pi}
                      </option>)}
                  </select>
                  <select className="text-xs border border-input rounded-md px-2.5 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={funderFilter || ''} onChange={e => setFunderFilter(e.target.value || null)} data-id="element-803">
                    <option value="" data-id="element-804">All Funders</option>
                    {uniqueFunders.map(f => <option key={f} value={f} data-id="element-805">
                        {f}
                      </option>)}
                  </select>
                  <select className="text-xs border border-input rounded-md px-2.5 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors" value={deptFilter || ''} onChange={e => setDeptFilter(e.target.value || null)} data-id="element-806">
                    <option value="" data-id="element-807">All Depts</option>
                    {uniqueDepts.map(d => <option key={d} value={d} data-id="element-808">
                        {d}
                      </option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap" data-id="element-809">
                    <input type="checkbox" checked={hideSubmitted} onChange={e => setHideSubmitted(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5" data-id="element-810" />
                    Hide submitted
                  </label>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-811">
                  <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums" data-id="element-812">
                    {filteredProposals.length} of {adminProposals.length}
                  </span>
                </div>
              </div>
            </div>

            {activeFilterPills.length > 0 && <div className="max-w-[1600px] mx-auto w-full px-5 pb-2.5" data-id="element-813">
                <div className="flex items-center gap-1.5 flex-wrap" data-id="element-814">
                  <ListFilter className="h-3 w-3 text-muted-foreground flex-shrink-0" data-id="element-815" />
                  {activeFilterPills.map(pill => <button key={pill.label} onClick={pill.onRemove} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium hover:bg-indigo-100 transition-colors group" data-id="element-816">
                      {pill.label}
                      <X className="h-2.5 w-2.5 opacity-60 group-hover:opacity-100" data-id="element-817" />
                    </button>)}
                  <button onClick={clearFilters} className="text-[11px] text-muted-foreground hover:text-foreground font-medium ml-1" data-id="element-818">
                    Clear all
                  </button>
                </div>
              </div>}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto" data-id="element-819">
            <div className="max-w-[1600px] mx-auto w-full" data-id="element-820">
              <table className="w-full text-sm text-left" data-id="element-821">
                <thead className="sticky top-0 z-10" data-id="element-822">
                  <tr className="border-b bg-slate-50/80 backdrop-blur-sm" data-id="element-823">
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-824">
                      Proposal
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-825">
                      PI / Dept
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-826">
                      Max Budget
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-827">
                      Status
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-828">
                      Deadline
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider w-36" data-id="element-829">
                      Documents
                    </th>
                    <th className="px-4 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-830">
                      Next Milestone
                    </th>
                    <th className="px-2 py-2.5 w-10" data-id="element-831"></th>
                    <th className="pl-1 pr-3 py-2.5 font-medium text-[11px] text-muted-foreground uppercase tracking-wider text-right w-16" data-id="element-832"></th>
                  </tr>
                </thead>
                <tbody data-id="element-833">
                  {filteredProposals.length === 0 ? <tr data-id="element-834">
                      <td colSpan={9} className="px-4 py-12 text-center" data-id="element-835">
                        <div className="flex flex-col items-center gap-2" data-id="element-836">
                          <Search className="h-8 w-8 text-muted-foreground/40" data-id="element-837" />
                          <p className="text-sm text-muted-foreground" data-id="element-838">
                            No proposals match your filters
                          </p>
                          <button onClick={clearFilters} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-839">
                            Clear all filters
                          </button>
                        </div>
                      </td>
                    </tr> : filteredProposals.map((proposal, rowIndex) => {
                  const days = daysUntil(proposal.deadlineISO);
                  const isSubmitted = proposal.status === 'Submitted';
                  const isUrgent = days <= 14 && proposal.status !== 'Submitted' && proposal.status !== 'Under Review';
                  const milestone = milestoneData[proposal.id];
                  const milestoneDays = milestone ? daysUntil(milestone.dueDate) : null;
                  const failingChecks = proposal.aiCheckStatus !== 'all-passed' && proposal.aiCheckStatus !== 'not-checked' ? proposal.aiIssueCount : 0;
                  const isFirstRow = rowIndex === 0;
                  return <tr key={proposal.id} className={`border-b border-border/60 hover:bg-indigo-50/30 transition-colors cursor-pointer group ${proposal.riskStatus === 'at-risk' ? 'bg-red-50/20' : ''}`} onClick={() => {
                    localStorage.setItem('viewMode', 'admin');
                    navigate(`/proposals/${proposal.id}/artifacts`);
                  }} data-id="element-840">
                          {/* Proposal */}
                          <td className="px-4 py-2.5 max-w-[280px]" data-id="element-841">
                            <div className="font-medium text-foreground text-[13px] leading-snug truncate group-hover:text-indigo-700 transition-colors" data-id="element-842">
                              {proposal.title}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1" data-id="element-843">
                              <span className="text-[11px] text-muted-foreground" data-id="element-844">
                                {proposal.funder}
                              </span>
                              <span className="text-muted-foreground/40" data-id="element-845">
                                ·
                              </span>
                              <span className="text-[11px] text-muted-foreground" data-id="element-846">
                                {proposal.mechanism}
                              </span>
                            </div>
                          </td>

                          {/* PI / Dept */}
                          <td className="px-4 py-2.5" data-id="element-847">
                            <div className="min-w-0" data-id="element-848">
                              <div className="text-[13px] font-medium text-foreground truncate" data-id="element-849">
                                {proposal.piName}
                              </div>
                              <div className="text-[11px] text-muted-foreground truncate" data-id="element-850">
                                {proposal.department}
                              </div>
                            </div>
                          </td>

                          {/* Max Budget */}
                          <td className="px-4 py-2.5" data-id="element-851">
                            <div className="text-[13px] font-medium text-foreground" data-id="element-852">
                              {proposal.maxBudget}
                            </div>
                            <div className="text-[11px] text-muted-foreground" data-id="element-853">
                              {proposal.grantLength}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-2.5" data-id="element-854">
                            <div className="flex flex-col gap-1.5 items-start" data-id="element-855">
                              <div className="flex items-center gap-1.5" data-id="element-856">
                                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${proposal.status === 'Submitted' ? 'bg-blue-500' : proposal.status === 'Under Review' ? 'bg-purple-500' : proposal.status === 'Needs Review' ? 'bg-emerald-500' : proposal.status === 'Draft' ? 'bg-slate-400' : 'bg-amber-500'}`} data-id="element-857" />
                                <span className="text-[12px] whitespace-nowrap text-foreground font-[500]" data-id="element-858">
                                  {proposal.status === 'Needs Review' ? 'Ready' : proposal.status}
                                </span>
                              </div>
                              {!isSubmitted && <Badge variant="outline" className={`text-[10px] font-semibold px-1.5 py-0 h-[18px] gap-1 whitespace-nowrap ${getRiskBadgeStyle(proposal.riskStatus)}`} data-id="element-859">
                                  <Sparkles className={`h-2.5 w-2.5 ${getRiskSparkleColor(proposal.riskStatus)}`} data-id="element-860" />
                                  {getRiskLabel(proposal.riskStatus)}
                                </Badge>}
                            </div>
                          </td>

                          {/* Deadline */}
                          <td className="px-4 py-2.5" data-id="element-861">
                            <div className="text-[13px] text-foreground" data-id="element-862">
                              {proposal.deadline}
                            </div>
                            {!isSubmitted && <div className={`text-[11px] ${isUrgent ? 'text-red-600 font-semibold' : days <= 30 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`} data-id="element-863">
                                {days}d remaining
                              </div>}
                          </td>

                          {/* Artifacts */}
                          <td className="px-4 py-2.5" data-id="element-864">
                            <div className="space-y-1.5" data-id="element-865">
                              <div className="flex items-center gap-2" data-id="element-866">
                                <Progress value={proposal.artifactsCompleted / proposal.artifactsTotal * 100} className="h-1.5 bg-slate-100 flex-1" data-id="element-867" />
                                <span className="text-[11px] text-muted-foreground whitespace-nowrap tabular-nums" data-id="element-868">
                                  {proposal.artifactsCompleted}/
                                  {proposal.artifactsTotal}
                                </span>
                              </div>
                              {proposal.aiCheckStatus === 'all-passed' ? <div className="flex items-center gap-1 text-emerald-600" data-id="element-869">
                                  <CheckCircle2 className="h-3 w-3" data-id="element-870" />
                                  <span className="text-[10px] font-medium" data-id="element-871">
                                    All checks passed
                                  </span>
                                </div> : proposal.aiCheckStatus === 'not-checked' ? <div className="flex items-center gap-1 text-muted-foreground" data-id="element-872">
                                  <CircleDashed className="h-3 w-3" data-id="element-873" />
                                  <span className="text-[10px]" data-id="element-874">
                                    Not checked
                                  </span>
                                </div> : failingChecks > 0 ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] px-1.5 py-0 h-[18px] gap-1" data-id="element-875">
                                  <Sparkles className="h-2.5 w-2.5" data-id="element-876" />
                                  {failingChecks}{' '}
                                  {failingChecks === 1 ? 'issue' : 'issues'}
                                </Badge> : null}
                            </div>
                          </td>

                          {/* Next Milestone */}
                          <td className="px-4 py-2.5 max-w-[220px]" data-id="element-877">
                            {isSubmitted ? <span className="text-[11px] text-muted-foreground" data-id="element-878">
                                —
                              </span> : milestone ? <div data-id="element-879">
                                <div className="text-[11px] text-foreground leading-snug truncate" data-id="element-880">
                                  <span className={`font-semibold tabular-nums ${milestoneDays != null && milestoneDays <= 7 ? 'text-amber-600' : 'text-muted-foreground'}`} data-id="element-881">
                                    {milestoneDays}d:
                                  </span>{' '}
                                  {milestone.label}
                                </div>
                                {milestone.reminderDays != null && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0 h-[18px] gap-1 mt-0.5" data-id="element-882">
                                    <Sparkles className="h-2.5 w-2.5" data-id="element-883" />
                                    Reminder in {milestone.reminderDays}d
                                  </Badge>}
                              </div> : proposal.status === 'Under Review' || proposal.status === 'Needs Review' ? <span className="text-[11px] text-muted-foreground" data-id="element-884">
                                Awaiting review
                              </span> : <span className="text-[11px] text-muted-foreground" data-id="element-885">
                                —
                              </span>}
                          </td>

                          {/* Activity History Button */}
                          <td className="px-2 py-2.5" data-id="element-886">
                            <button onClick={e => handleActivityButtonClick(e, proposal.id, isFirstRow)} title="Activity history" className={`inline-flex items-center justify-center h-6 w-6 rounded-md border transition-colors ${activityPopoverProposalId === proposal.id ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'border-border hover:bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-887">
                              <CalendarDays className="h-3.5 w-3.5" data-id="element-888" />
                            </button>
                          </td>

                          {/* Export action */}
                          <td className="px-2 py-2.5 text-center" data-id="element-889">
                            <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2 transition-opacity" onClick={e => {
                        e.stopPropagation();
                        setSelectedProposal(proposal);
                      }} data-id="element-890">
                              <Download className="h-3 w-3" data-id="element-891" />
                              Export
                            </Button>
                          </td>
                        </tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Activity Feed Sidebar */}
        <div className={`fixed top-14 right-0 bottom-0 w-80 bg-white border-l border-border flex flex-col z-30 shadow-xl transition-transform duration-300 ease-in-out ${isActivityFeedOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-892">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between" data-id="element-893">
            <h3 className="font-semibold text-xs text-foreground flex items-center gap-1.5 uppercase tracking-wider" data-id="element-894">
              <Activity className="h-3.5 w-3.5 text-indigo-600" data-id="element-895" />
              Activity
            </h3>
            <div className="flex items-center gap-2" data-id="element-896">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-medium" data-id="element-897">
                Live
              </Badge>
              <button onClick={() => setIsActivityFeedOpen(false)} className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" data-id="element-898">
                <X className="h-3.5 w-3.5" data-id="element-899" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-900">
            {activityFeed.map((item, idx) => <div key={`${item.id}-${idx}`} className="px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => {
            localStorage.setItem('viewMode', 'admin');
            navigate(`/proposals/${item.id}/artifacts`);
          }} data-id="element-901">
                <div className="flex items-start gap-2.5" data-id="element-902">
                  <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${item.riskStatus === 'at-risk' ? 'bg-red-500' : item.riskStatus === 'needs-attention' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-903" />
                  <div className="min-w-0 flex-1" data-id="element-904">
                    <p className="text-[12px] font-medium text-foreground leading-snug" data-id="element-905">
                      {item.activity}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5" data-id="element-906">
                      {item.piName} · {item.title.split(':')[0]}
                    </p>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5" data-id="element-907">
                      {item.time}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {isActivityFeedOpen && <div className="fixed inset-0 top-14 bg-black/10 z-20 transition-opacity" onClick={() => setIsActivityFeedOpen(false)} data-id="element-908" />}
      </div>

      {selectedProposal && <ExportPacketModal proposal={selectedProposal} open={!!selectedProposal} onClose={() => setSelectedProposal(null)} data-id="element-909" />}

      {/* Activity History Popover */}
      {activityPopoverProposalId && activityPopoverPos && <>
          <div className="fixed inset-0 z-40" onClick={() => {
        setActivityPopoverProposalId(null);
        setActivityPopoverPos(null);
      }} data-id="element-910" />
          <div className="fixed z-50 w-96 bg-white rounded-lg border border-border shadow-2xl flex flex-col overflow-hidden" style={{
        top: activityPopoverPos.top,
        left: activityPopoverPos.left,
        maxHeight: '480px'
      }} onClick={e => e.stopPropagation()} data-id="element-911">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0" data-id="element-912">
              <div className="flex items-center gap-2" data-id="element-913">
                <CalendarDays className="h-4 w-4 text-indigo-600" data-id="element-914" />
                <span className="text-sm font-semibold text-foreground" data-id="element-915">
                  Activity History
                </span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-muted-foreground" data-id="element-916">
                  {demoActivityHistory.length} events
                </Badge>
              </div>
              <button onClick={() => {
            setActivityPopoverProposalId(null);
            setActivityPopoverPos(null);
          }} className="h-6 w-6 rounded-md hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" data-id="element-917">
                <X className="h-3.5 w-3.5" data-id="element-918" />
              </button>
            </div>
            {/* Proposal context */}
            <div className="px-4 py-2 bg-muted/30 border-b border-border flex-shrink-0" data-id="element-919">
              <p className="text-[11px] font-medium text-foreground truncate" data-id="element-920">
                DOD CDMRP: Traumatic Brain Injury Biomarker Discovery Program
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5" data-id="element-921">
                Dr. James Park · Neuroscience
              </p>
            </div>
            {/* Timeline */}
            <div className="flex-1 overflow-y-auto px-4 py-3" data-id="element-922">
              <div className="relative" data-id="element-923">
                {/* Vertical line */}
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" data-id="element-924" />
                <div className="space-y-0" data-id="element-925">
                  {demoActivityHistory.map((item, idx) => {
                const cfg = activityTypeConfig[item.type] || activityTypeConfig.action;
                const Icon = cfg.icon;
                const showDate = idx === 0 || demoActivityHistory[idx - 1].date !== item.date;
                return <div key={item.id} data-id="element-926">
                        {showDate && <div className="flex items-center gap-2 mb-2 mt-3 first:mt-0 pl-6" data-id="element-927">
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-928">
                              {item.date}
                            </span>
                          </div>}
                        <div className="flex items-start gap-3 pb-3" data-id="element-929">
                          <div className={`h-3.5 w-3.5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center border-2 border-white ${cfg.dotCls} z-10 relative`} data-id="element-930" />
                          <div className="flex-1 min-w-0" data-id="element-931">
                            <p className="text-[12px] text-foreground leading-snug" data-id="element-932">
                              {item.event}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5" data-id="element-933">
                              <span className="text-[10px] font-medium text-muted-foreground" data-id="element-934">
                                {item.actor}
                              </span>
                              <span className="text-muted-foreground/40 text-[10px]" data-id="element-935">
                                ·
                              </span>
                              <span className="text-[10px] text-muted-foreground/70" data-id="element-936">
                                {item.role}
                              </span>
                              <span className="text-muted-foreground/40 text-[10px]" data-id="element-937">
                                ·
                              </span>
                              <span className="text-[10px] text-muted-foreground/60" data-id="element-938">
                                {item.time}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}